import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, primeCsrf } from './api';
import { useMe } from './auth';
import type { PortalUserData } from '@/types/api';

/**
 * Portal permissions are granted per user, per client (on the client_user
 * link). The effective set for the current user comes from /v1/me; admins
 * hold everything. Admin grant UI edits a client's portal users.
 */

/** Catalog for the admin grant UI — grouped by resource. Mirrors PortalPermission. */
export const PORTAL_PERMISSIONS: Array<{
  key: string;
  group: string;
  label: string;
}> = [
  { key: 'projects', group: 'Projects', label: 'View & collaborate on projects' },
  { key: 'assets', group: 'IT Assets', label: 'View IT assets' },
  { key: 'blog', group: 'Blog', label: 'Write & submit blog posts' },
  { key: 'proposals.view', group: 'Proposals', label: 'View proposals' },
  { key: 'proposals.act', group: 'Proposals', label: 'Accept or decline proposals' },
  { key: 'invoices.view', group: 'Invoices', label: 'View invoices' },
  { key: 'invoices.pay', group: 'Invoices', label: 'Pay invoices online' },
  { key: 'documents', group: 'Documents', label: 'View & download documents' },
  { key: 'credentials', group: 'Passwords', label: 'View & reveal saved passwords' },
  { key: 'subscriptions', group: 'Subscriptions', label: 'View subscriptions & retainers' },
  { key: 'contacts', group: 'Team', label: 'View team & contacts' },
  { key: 'activity', group: 'Activity', label: 'View account activity' },
  { key: 'requests', group: 'Requests', label: 'Submit & track requests' },
];

export function usePortalPermissions(): string[] {
  return useMe().data?.portal_permissions ?? [];
}

export function useHasPermission(key: string): boolean {
  return usePortalPermissions().includes(key);
}

const USERS_KEY = (clientId: number) => ['clients', clientId, 'portal-users'];

export function usePortalUsers(clientId: number | undefined) {
  return useQuery({
    queryKey: clientId ? USERS_KEY(clientId) : ['clients', 'none', 'portal-users'],
    enabled: clientId != null,
    queryFn: async (): Promise<PortalUserData[]> => {
      const { data } = await api.get<PortalUserData[]>(`/v1/clients/${clientId}/portal-users`);
      return data;
    },
  });
}

type PortalUserPatch = { permissions?: string[]; title_at_client?: string | null };

export function useUpdatePortalUser(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      userId,
      patch,
    }: {
      userId: number;
      patch: PortalUserPatch;
    }): Promise<PortalUserData> => {
      await primeCsrf();
      const { data } = await api.patch<PortalUserData>(
        `/v1/clients/${clientId}/portal-users/${userId}`,
        patch,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: USERS_KEY(clientId) }),
  });
}

export function useRemovePortalUser(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: number): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/clients/${clientId}/portal-users/${userId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: USERS_KEY(clientId) }),
  });
}
